// IndexedDB-backed persistence for per-agent chat history.
//
// One object store, keyed by agent id. The full ChatHistory blob is read on
// agent switch and written debounced (200ms) on every mutation. Attachment
// blob URLs are *not* persisted — they're created lazily when the user opens
// an inline image preview.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { NiuMessage } from '../protocol/types';

export interface ChatHistory {
  /** Append-only message log, in receive order. */
  messages: NiuMessage[];
  /** messageId → list of emoji shortcodes. */
  reactions: Record<string, string[]>;
  /** messageId → unix ms timestamp of arrival/send. */
  timestamps: Record<string, number>;
  /** messageId → delivery status (only populated for outbound user messages). */
  deliveryStatus: Record<string, 'sending' | 'sent' | 'failed'>;
  /** questionId → selected option value (locks the question card). */
  answers: Record<string, string>;
}

interface NiuDB extends DBSchema {
  chats: {
    key: string;
    value: ChatHistory;
  };
  meta: {
    key: string;
    value: unknown;
  };
}

const DB_NAME = 'niu-web';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<NiuDB>> | null = null;

function db(): Promise<IDBPDatabase<NiuDB>> {
  if (!dbPromise) {
    dbPromise = openDB<NiuDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('chats')) {
          db.createObjectStore('chats');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta');
        }
      },
    });
  }
  return dbPromise;
}

export function emptyHistory(): ChatHistory {
  return {
    messages: [],
    reactions: {},
    timestamps: {},
    deliveryStatus: {},
    answers: {},
  };
}

export async function loadHistory(agentId: string): Promise<ChatHistory> {
  const conn = await db();
  const stored = await conn.get('chats', agentId);
  return stored ?? emptyHistory();
}

export async function saveHistory(agentId: string, history: ChatHistory): Promise<void> {
  const conn = await db();
  await conn.put('chats', history, agentId);
}

export async function listAgentIds(): Promise<string[]> {
  const conn = await db();
  return (await conn.getAllKeys('chats')) as string[];
}

export async function clearHistory(agentId: string): Promise<void> {
  const conn = await db();
  await conn.delete('chats', agentId);
}

// ---- Generic meta key/value (cursors, flags, etc.) ----

export async function metaGet<T = unknown>(key: string): Promise<T | undefined> {
  const conn = await db();
  return (await conn.get('meta', key)) as T | undefined;
}

export async function metaSet(key: string, value: unknown): Promise<void> {
  const conn = await db();
  await conn.put('meta', value, key);
}

// ---- Debounced writer (per agent) ----

const pendingWriteTimers = new Map<string, number>();
const DEBOUNCE_MS = 200;

export function scheduleSave(agentId: string, history: ChatHistory): void {
  const existing = pendingWriteTimers.get(agentId);
  if (existing !== undefined) clearTimeout(existing);
  const timer = setTimeout(() => {
    pendingWriteTimers.delete(agentId);
    void saveHistory(agentId, history);
  }, DEBOUNCE_MS) as unknown as number;
  pendingWriteTimers.set(agentId, timer);
}

/** Force-flush any debounced writes (e.g. on disconnect or page unload). */
export async function flushAll(allHistories: Record<string, ChatHistory>): Promise<void> {
  const ids = Array.from(pendingWriteTimers.keys());
  for (const id of ids) {
    const t = pendingWriteTimers.get(id);
    if (t !== undefined) clearTimeout(t);
    pendingWriteTimers.delete(id);
    const h = allHistories[id];
    if (h) await saveHistory(id, h);
  }
}
